import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings as SettingsIcon, Moon, Sun, User, MessageCircle, Check, Diamond, AlertTriangle, Send, Store, ShoppingBag, Bitcoin, DollarSign, Trash2, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme, themes } from '../context/ThemeContext';
import { useNotification } from '../context/NotificationContext';
import api from '../api/axios';

const Settings = () => {
  const { user, logout, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const notificationContext = useNotification();
  
  // Create a safe notify object with fallbacks
  const notify = React.useMemo(() => ({
    success: (msg, duration) => {
      if (notificationContext?.success) {
        notificationContext.success(msg, duration);
      } else {
        console.log('SUCCESS:', msg);
        alert(msg);
      }
    },
    error: (msg, duration) => {
      if (notificationContext?.error) {
        notificationContext.error(msg, duration);
      } else {
        console.error('ERROR:', msg);
        alert(msg);
      }
    },
    warning: (msg, duration) => {
      if (notificationContext?.warning) {
        notificationContext.warning(msg, duration);
      } else {
        console.warn('WARNING:', msg);
        alert(msg);
      }
    },
    confirm: async (msg, title) => {
      if (notificationContext?.confirm) {
        return await notificationContext.confirm(msg, title);
      } else {
        console.log('CONFIRM:', title, msg);
        return window.confirm(msg);
      }
    }
  }), [notificationContext]);
  
  // Debug logging
  React.useEffect(() => {
    console.log('Settings mounted, notificationContext:', notificationContext);
    console.log('Settings mounted, notify:', notify);
  }, [notificationContext, notify]);
  const [proxyUrl, setProxyUrl] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [activeTab, setActiveTab] = useState('deposit');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [gateName, setGateName] = useState('');
  const [gateDomain, setGateDomain] = useState('');
  const [messageText, setMessageText] = useState('');
  const [savedGates, setSavedGates] = useState([]);
  const [depositTickets, setDepositTickets] = useState([]);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [ticketTimer, setTicketTimer] = useState(0);
  const [savedSKs, setSavedSKs] = useState([]);
  const [savedPPs, setSavedPPs] = useState([]);
  const [premiumTickets, setPremiumTickets] = useState([]);


  // Load user's existing proxy from API (real-time fetch)
  React.useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await api.get('/user/profile');
        if (response.data.success && response.data.user.user_proxy) {
          if (response.data.user.user_proxy !== 'N/A') {
            setProxyUrl(response.data.user.user_proxy);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user data');
      }
    };
    
    fetchUserData();
    fetchSavedGates();
    fetchDepositTickets();
    fetchPremiumTickets();
    fetchSavedSKs();
    fetchSavedPPs();
  }, []);
  
  const fetchSavedSKs = async () => {
    try {
      const response = await api.get('/user/sk-configs');
      if (response.data.success) {
        setSavedSKs(response.data.configs);
      }
    } catch (error) {
      console.error('Failed to fetch SK configs');
    }
  };

  const fetchSavedPPs = async () => {
    try {
      const response = await api.get('/user/pp-configs');
      if (response.data.success) {
        setSavedPPs(response.data.configs);
      }
    } catch (error) {
      console.error('Failed to fetch PP configs');
    }
  };
  
  const handleDeleteSK = async (id) => {
    const confirmed = await notify.confirm(
      'Delete this SK configuration?',
      'Confirm Delete'
    );
    
    if (!confirmed) return;
    
    try {
      const response = await api.delete(`/user/sk-config/${id}`);
      if (response.data.success) {
        notify.success('SK configuration deleted', 3000);
        fetchSavedSKs();
      }
    } catch (error) {
      notify.error(error.response?.data?.error || 'Failed to delete', 4000);
    }
  };

  const handleDeletePP = async (id) => {
    const confirmed = await notify.confirm(
      'Delete this PP configuration?',
      'Confirm Delete'
    );
    
    if (!confirmed) return;
    
    try {
      const response = await api.delete(`/user/pp-config/${id}`);
      if (response.data.success) {
        notify.success('PP configuration deleted', 3000);
        fetchSavedPPs();
      }
    } catch (error) {
      notify.error(error.response?.data?.error || 'Failed to delete', 4000);
    }
  };
  
  // Timer for ticket expiry
  React.useEffect(() => {
    if (currentTicket && currentTicket.expires_at) {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const expiry = new Date(currentTicket.expires_at).getTime();
        const remaining = Math.max(0, Math.floor((expiry - now) / 1000));
        setTicketTimer(remaining);

        if (remaining === 0) {
          setCurrentTicket(null);
          setSelectedPayment(null);
          notify.warning('Payment ticket expired', 3000);
          fetchDepositTickets();
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [currentTicket, notify]);
  
  const fetchDepositTickets = async () => {
    try {
      const response = await api.get('/payment/tickets');
      if (response.data.success) {
        setDepositTickets(response.data.tickets.filter(t => t.purpose === 'deposit'));
      }
    } catch (error) {
      console.error('Failed to fetch tickets');
    }
  };

  const fetchPremiumTickets = async () => {
    try {
      const response = await api.get('/payment/tickets');
      if (response.data.success) {
        setPremiumTickets(response.data.tickets.filter(t => t.purpose === 'premium'));
      }
    } catch (error) {
      console.error('Failed to fetch premium tickets');
    }
  };
  
  const handleCreateDepositTicket = async () => {
    const amount = parseFloat(selectedPayment);
    if (!amount || amount <= 0) {
      notify.warning('Please enter a valid amount', 3000);
      return;
    }
    
    try {
      const response = await api.post('/payment/ticket', {
        amount,
        purpose: 'deposit'
      });
      
      if (response.data.success) {
        setCurrentTicket(response.data.ticket);
        notify.success('Payment ticket created!', 3000);
        fetchDepositTickets();
      }
    } catch (error) {
      notify.error(error.response?.data?.error || 'Failed to create ticket', 4000);
    }
  };
  
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const fetchSavedGates = async () => {
    try {
      const response = await api.get('/user/auto-sites');
      if (response.data.success) {
        setSavedGates(response.data.packages);
      }
    } catch (error) {
      console.error('Failed to fetch gates');
    }
  };
  
  const handleDeleteGate = async (packageName) => {
    const confirmed = await notify.confirm(
      `Delete ${packageName.toUpperCase()} gate configuration?`,
      'Confirm Delete'
    );
    
    if (!confirmed) return;
    
    try {
      const response = await api.delete(`/user/auto-sites/${packageName}`);
      if (response.data.success) {
        notify.success('Gate deleted successfully', 3000);
        fetchSavedGates();
      }
    } catch (error) {
      notify.error(error.response?.data?.error || 'Failed to delete gate', 4000);
    }
  };

  const handleVerifyAndSave = async () => {
    if (!proxyUrl || proxyUrl.trim() === '') {
      notify.warning('Please enter a proxy');
      return;
    }

    setVerifying(true);
    try {
      const response = await api.post('/user/update-proxy', { proxy: proxyUrl });
      if (response.data.success) {
        notify.success(response.data.message);
      }
    } catch (error) {
      notify.error(error.response?.data?.error || 'Failed to verify proxy');
    } finally {
      setVerifying(false);
    }
  };

  const plans = [
    { name: 'Free', plan_id: 'free', duration: 'Forever', price: '$0', priceNum: 0, credits: '100 Credits (Register Bonus)', premium: 'Limited APIs Only', support: 'Slow Speed', bonus: '', buttonColor: 'from-gray-600 to-gray-700', cardGradient: 'from-gray-900/20 to-gray-800/20', borderColor: 'border-gray-500/30', emoji: '🆓', isFree: true },
    { name: 'Bronze', plan_id: 'bronze_1d', duration: '1 Day', price: '$3', priceNum: 3, credits: '500 Credits', premium: 'Shopii #2-4 + PayPal', support: 'Slow & Normal Speed', bonus: '+$0.5 Bonus', buttonColor: 'from-orange-600 to-orange-700', cardGradient: 'from-orange-900/20 to-orange-800/20', borderColor: 'border-orange-500/30', emoji: '🥉', durationDays: 1 },
    { name: 'Silver', plan_id: 'silver_7d', duration: '7 Days', price: '$10', priceNum: 10, credits: '1500 Credits', premium: 'Shopii #2-4 + PayPal', support: 'Slow & Normal Speed', bonus: '+$1.5 Bonus', buttonColor: 'from-gray-400 to-gray-500', cardGradient: 'from-gray-900/30 to-gray-800/30', borderColor: 'border-gray-500/30', emoji: '🥈', durationDays: 7 },
    { name: 'Gold', plan_id: 'gold_15d', duration: '15 Days', price: '$20', priceNum: 20, credits: '2500 Credits', premium: 'All APIs Unlocked', support: 'All Speeds', bonus: '+$3.5 Bonus', buttonColor: 'from-yellow-400 to-yellow-500', cardGradient: 'from-yellow-900/30 to-yellow-800/30', borderColor: 'border-yellow-500/30', emoji: '🥇', durationDays: 15 },
    { name: 'Diamond', plan_id: 'diamond_30d', duration: '30 Days', price: '$35', priceNum: 35, credits: '4000 Credits', premium: 'All APIs Unlocked', support: 'All Speeds', bonus: '+$5 Bonus', buttonColor: 'from-cyan-400 to-blue-500', cardGradient: 'from-cyan-900/30 to-blue-800/30', borderColor: 'border-cyan-500/30', emoji: '💎', durationDays: 30 },
  ];

  // Helper function to get plan index
  const getPlanIndex = (planName) => {
    const index = plans.findIndex(p => p.name === planName);
    return index !== -1 ? index : 0; // Return 0 (Free) if not found
  };

  const currentPlanIndex = getPlanIndex(user?.plan || 'Free');
  const isCurrentPlan = (plan) => plan.name === user?.plan || (plan.isFree && user?.status === 'FREE');
  const isHigherPlan = (plan) => !plan.isFree && getPlanIndex(plan.name) > currentPlanIndex;
  const isLowerPlan = (plan) => !plan.isFree && getPlanIndex(plan.name) < currentPlanIndex;

  const handleCreateGate = async () => {
    if (!gateName || !gateDomain) {
      notify.warning('Please fill in all fields');
      return;
    }
    try {
      await api.post('/user/custom-gates', { gateName, domain: gateDomain, gateType: 'SHOPII' });
      notify.success('Gate created successfully!');
      setGateName('');
      setGateDomain('');
    } catch (error) {
      notify.error(error.response?.data?.error || 'Failed to create gate');
    }
  };

  const handlePurchasePlan = async (plan) => {
    // Skip free plan
    if (plan.isFree) return;

    const userCash = parseFloat(user?.cash || 0);
    const isUpgrade = isHigherPlan(plan);
    
    // Always charge FULL price (no discount for upgrades)
    const costAmount = plan.priceNum;
    let message = `Purchase ${plan.name} ${plan.emoji} plan for ${plan.price}?`;
    
    if (isUpgrade && user?.plan && user?.plan !== 'N/A') {
      const currentPlan = plans.find(p => p.name === user.plan);
      if (currentPlan) {
        message = `Upgrade from ${currentPlan.name} ${currentPlan.emoji} to ${plan.name} ${plan.emoji}?\n\nUpgrade Cost: ${plan.price}\n\nYour ${plan.duration} will be added to your existing premium time.`;
      }
    }
    
    // Check if user has enough cash
    if (userCash < costAmount) {
      notify.error(`Insufficient balance! You need ${plan.price} but only have $${userCash.toFixed(2)}`, 5000);
      return;
    }
    
    // Confirm purchase
    const confirmed = await notify.confirm(
      `${message}\n\nYou will receive:\n• ${plan.credits}\n• ${isUpgrade ? 'Additional ' + plan.duration : plan.duration} Premium Access\n${plan.bonus ? '• ' + plan.bonus : ''}`,
      isUpgrade ? 'Confirm Upgrade' : 'Confirm Purchase'
    );
    
    if (!confirmed) return;

    try {
      const response = await api.post('/payment/buy-premium', { plan_id: plan.plan_id });
      
      if (response.data.success) {
        console.log('✅ Purchase successful, response:', response.data);
        
        // Play success sound
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGGS57OihUhELTKXh8bllHgU7k9nyyXooBSV3yPDblUELEly06+qnUxMLRp/g8r5sIQYscM7y2ok3CBhju+zooVIRC0yl4fG5ZR4');
          audio.volume = 0.5;
          audio.play().catch(() => {});
        } catch (e) {
          // Ignore audio errors
        }

        // Refresh user data multiple times to ensure update
        console.log('🔄 Refreshing user data (attempt 1)...');
        await refreshUser();
        
        // Wait a moment and refresh again to be sure
        await new Promise(resolve => setTimeout(resolve, 1000));
        console.log('🔄 Refreshing user data (attempt 2)...');
        await refreshUser();
        
        await fetchPremiumTickets();
        console.log('✅ Premium tickets fetched');
        
        notify.success(`${plan.name} ${plan.emoji} activated successfully! 🎉`, 5000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Purchase failed';
      console.error('❌ Purchase error:', error);
      notify.error(errorMsg, 5000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1b2a] to-[#0a1628] pb-24">
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <SettingsIcon className="text-cyan-400" size={24} />
            <h1 className="text-2xl font-bold text-white">Settings</h1>
          </div>
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="w-10 h-10 rounded-full bg-gray-900/50 flex items-center justify-center border border-gray-700/50 shadow-md">
            {theme === 'light' ? <Sun className="text-gray-400" size={20} /> : <Moon className="text-gray-400" size={20} />}
          </motion.button>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-5 border border-gray-700/50 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <SettingsIcon className="text-cyan-400" size={16} />
            </div>
            <h2 className="text-lg font-bold text-white">Proxy Settings</h2>
          </div>
          
          {user?.user_proxy && user.user_proxy !== 'N/A' && (
            <div className="mb-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-green-400 text-xs font-semibold mb-1">Current Proxy:</p>
              <p className="text-gray-300 font-mono text-sm break-all">{user.user_proxy}</p>
            </div>
          )}
          
          <input 
            type="text" 
            value={proxyUrl} 
            onChange={(e) => setProxyUrl(e.target.value)} 
            placeholder="host:port:user:pass" 
            className="w-full bg-gray-900/50 border-gray-700/50 text-gray-300 placeholder-gray-500 border rounded-lg px-4 py-3 text-sm mb-3 focus:outline-none focus:border-cyan-500/50" 
          />
          <motion.button 
            whileHover={{ scale: 1.02 }} 
            whileTap={{ scale: 0.98 }} 
            onClick={handleVerifyAndSave}
            disabled={verifying || !proxyUrl}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed border-none rounded-lg py-3 text-white font-medium flex items-center justify-center gap-2"
          >
            {verifying ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Verifying...
              </>
            ) : (
              <>
                <Check size={18} />
                Verify & Save
              </>
            )}
          </motion.button>
          <p className="text-gray-500 text-xs mt-2">
            Proxy will be tested before saving. Only working proxies are accepted.
          </p>
        </motion.div>

        <div className="flex gap-2 mb-6">
          {['deposit', 'premium', 'gates', 'message'].map((tab) => (
            <motion.button key={tab} whileTap={{ scale: 0.95 }} onClick={() => setActiveTab(tab)} className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition-colors border ${activeTab === tab ? 'bg-transparent text-white border-white' : 'bg-gray-800/50 text-gray-400 border-gray-700/50'}`}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </motion.button>
          ))}
        </div>


        {activeTab === 'deposit' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="text-cyan-400" size={20} />
              <h2 className="text-lg font-bold text-white">Deposit Cash</h2>
            </div>
            <p className="text-gray-400 text-sm mb-6">Add cash to your wallet via cryptocurrency</p>

            {!currentTicket ? (
              <>
                {/* Quick Amount Buttons */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[10, 25, 50, 100, 250, 500].map((amount) => (
                    <motion.button
                      key={amount}
                      onClick={() => setSelectedPayment(amount.toString())}
                      whileTap={{ scale: 0.95 }}
                      className={`py-3 border rounded-xl font-semibold transition-all ${
                        selectedPayment === amount.toString()
                          ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50'
                          : 'bg-gray-800/50 text-white border-gray-700/50 hover:bg-cyan-500/20 hover:border-cyan-500/50'
                      }`}
                    >
                      ${amount}
                    </motion.button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="bg-gray-800/40 backdrop-blur-xl rounded-2xl p-4 border border-gray-700/50 mb-4">
                  <label className="text-gray-400 text-sm mb-2 block">Custom Amount (USD)</label>
                  <input
                    type="number"
                    value={selectedPayment || ''}
                    onChange={(e) => setSelectedPayment(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full bg-gray-900/50 border-gray-700/50 text-white placeholder-gray-500 border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-cyan-500/50"
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCreateDepositTicket}
                  disabled={!selectedPayment || parseFloat(selectedPayment) <= 0}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed border-none rounded-lg py-3 text-white font-medium"
                >
                  Create Ticket
                </motion.button>
              </>
            ) : (
              <div className="space-y-4">
                {/* Timer */}
                <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Clock size={20} className="text-red-400" />
                    <span className="text-white font-bold text-2xl">{formatTimer(ticketTimer)}</span>
                  </div>
                  <p className="text-red-300 text-xs">Ticket expires in</p>
                </div>

                {/* Amount Display */}
                <div className="bg-gradient-to-br from-cyan-600/20 to-blue-600/20 backdrop-blur-xl rounded-2xl p-5 border border-cyan-500/30">
                  <p className="text-gray-400 text-xs mb-1">Amount to Deposit</p>
                  <p className="text-3xl font-bold text-white">${selectedPayment}</p>
                  <p className="text-gray-500 text-xs mt-1">Ticket ID: {currentTicket.ticket_id}</p>
                </div>

                {/* Payment Addresses */}
                <div className="bg-gray-800/40 backdrop-blur-xl rounded-2xl p-5 border border-gray-700/50">
                  <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                    <ShoppingBag size={18} className="text-cyan-400" />
                    Choose Payment Method
                  </h3>

                  <div className="space-y-3">
                    {/* Binance Pay */}
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Bitcoin size={18} className="text-yellow-400" />
                        <span className="text-white font-bold text-sm">𝐁𝐈𝐍𝐀𝐍𝐂𝐄 𝐈𝐃/𝐏𝐀𝐘</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-gray-950/50 px-3 py-2 rounded text-xs text-gray-300 font-mono">
                          753175553
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('753175553');
                            notify.success('Copied!', 2000);
                          }}
                          className="p-2 hover:bg-gray-700/50 rounded transition-colors"
                        >
                          <Check size={14} className="text-gray-400" />
                        </button>
                      </div>
                    </div>

                    {/* BTC */}
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Bitcoin size={18} className="text-orange-400" />
                        <span className="text-white font-bold text-sm">𝐁𝐓𝐂</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-gray-950/50 px-3 py-2 rounded text-xs text-gray-300 font-mono break-all">
                          1GNgQcMHfAYS3XVmAFhck959vGb3T1B86t
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('1GNgQcMHfAYS3XVmAFhck959vGb3T1B86t');
                            notify.success('Copied!', 2000);
                          }}
                          className="p-2 hover:bg-gray-700/50 rounded transition-colors flex-shrink-0"
                        >
                          <Check size={14} className="text-gray-400" />
                        </button>
                      </div>
                    </div>

                    {/* USDT TRC20 */}
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Bitcoin size={18} className="text-green-400" />
                        <span className="text-white font-bold text-sm">𝐔𝐒𝐃𝐓 [𝐓𝐑𝐂𝟐𝟎]</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-gray-950/50 px-3 py-2 rounded text-xs text-gray-300 font-mono break-all">
                          TGcizrCAjTvvLCAakd1KojTVWGZEC9eEm9
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('TGcizrCAjTvvLCAakd1KojTVWGZEC9eEm9');
                            notify.success('Copied!', 2000);
                          }}
                          className="p-2 hover:bg-gray-700/50 rounded transition-colors flex-shrink-0"
                        >
                          <Check size={14} className="text-gray-400" />
                        </button>
                      </div>
                    </div>

                    {/* USDT BEP20 */}
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Bitcoin size={18} className="text-yellow-400" />
                        <span className="text-white font-bold text-sm">𝐔𝐒𝐃𝐓 [𝐁𝐄𝐏𝟐𝟎]</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-gray-950/50 px-3 py-2 rounded text-xs text-gray-300 font-mono break-all">
                          0xcd76a1fddfc20c89b223442e9ea655d9ab3b0950
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('0xcd76a1fddfc20c89b223442e9ea655d9ab3b0950');
                            notify.success('Copied!', 2000);
                          }}
                          className="p-2 hover:bg-gray-700/50 rounded transition-colors flex-shrink-0"
                        >
                          <Check size={14} className="text-gray-400" />
                        </button>
                      </div>
                    </div>

                    {/* LTC */}
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Bitcoin size={18} className="text-blue-400" />
                        <span className="text-white font-bold text-sm">𝐋𝐓𝐂</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-gray-950/50 px-3 py-2 rounded text-xs text-gray-300 font-mono break-all">
                          LRgnqqufbX2euvmiyhBU26EaMZVWMicq9A
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('LRgnqqufbX2euvmiyhBU26EaMZVWMicq9A');
                            notify.success('Copied!', 2000);
                          }}
                          className="p-2 hover:bg-gray-700/50 rounded transition-colors flex-shrink-0"
                        >
                          <Check size={14} className="text-gray-400" />
                        </button>
                      </div>
                    </div>

                    {/* TRX */}
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Bitcoin size={18} className="text-red-400" />
                        <span className="text-white font-bold text-sm">𝐓𝐑𝐗</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-gray-950/50 px-3 py-2 rounded text-xs text-gray-300 font-mono break-all">
                          TGcizrCAjTvvLCAakd1KojTVWGZEC9eEm9
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('TGcizrCAjTvvLCAakd1KojTVWGZEC9eEm9');
                            notify.success('Copied!', 2000);
                          }}
                          className="p-2 hover:bg-gray-700/50 rounded transition-colors flex-shrink-0"
                        >
                          <Check size={14} className="text-gray-400" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Notice */}
                  <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={18} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-yellow-300 text-sm font-semibold mb-2">𝐈𝐦𝐩𝐨𝐫𝐭𝐚𝐧𝐭 𝐍𝐨𝐭𝐢𝐜𝐞</p>
                        <p className="text-gray-300 text-xs leading-relaxed">
                          𝐀𝐟𝐭𝐞𝐫 𝐜𝐨𝐦𝐩𝐥𝐞𝐭𝐢𝐧𝐠 𝐭𝐡𝐞 𝐩𝐚𝐲𝐦𝐞𝐧𝐭, Contact 𝐀𝐝𝐦𝐢𝐧, 𝐭𝐡𝐞𝐧 𝐬𝐞𝐧𝐝 𝐭𝐡𝐞 𝐭𝐫𝐚𝐧𝐬𝐚𝐜𝐭𝐢𝐨𝐧 𝐬𝐜𝐫𝐞𝐞𝐧𝐬𝐡𝐨𝐭 𝐰𝐢𝐭𝐡 𝐲𝐨𝐮𝐫 𝐓𝐞𝐥𝐞𝐠𝐫𝐚𝐦 𝐈𝐃.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Back Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedPayment(null);
                    setCurrentTicket(null);
                  }}
                  className="w-full bg-gray-700/50 hover:bg-gray-600/50 border-none rounded-lg py-3 text-white font-medium mt-4"
                >
                  Back
                </motion.button>
              </div>
            )}

            {/* Transaction History */}
            {depositTickets.length > 0 && !currentTicket && (
              <div className="mt-6">
                <h3 className="text-white font-semibold mb-3 text-sm">Transaction History</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {depositTickets.slice(0, 10).map((ticket) => (
                    <div
                      key={ticket.ticket_id}
                      className={`p-3 rounded-lg border ${
                        ticket.status === 'approved'
                          ? 'bg-green-900/20 border-green-500/30'
                          : ticket.status === 'pending'
                          ? 'bg-yellow-900/20 border-yellow-500/30'
                          : ticket.status === 'expired'
                          ? 'bg-gray-900/20 border-gray-500/30'
                          : 'bg-red-900/20 border-red-500/30'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <p className="text-white font-semibold text-sm">${ticket.amount}</p>
                          <p className="text-gray-500 text-xs">{new Date(ticket.created_at).toLocaleString()}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          ticket.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                          ticket.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          ticket.status === 'expired' ? 'bg-gray-500/20 text-gray-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {ticket.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-gray-600 text-xs">{ticket.ticket_id}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'premium' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="flex items-center gap-2 mb-4">
              <Diamond className="text-cyan-400" size={20} />
              <h2 className="text-lg font-bold text-white">Premium Plans</h2>
            </div>
            <p className="text-gray-400 text-sm mb-6">Upgrade your account with premium features and extra credits</p>

            <div className="space-y-4 mb-6">
              {plans.map((plan, index) => (
                <motion.div key={plan.name} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + index * 0.1 }} className={`bg-gradient-to-br ${plan.cardGradient} backdrop-blur-xl rounded-2xl p-5 border ${plan.borderColor} relative overflow-hidden shadow-lg`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                      <p className="text-gray-400 text-sm">{plan.duration}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-white">{plan.price}</p>
                      <p className="text-gray-400 text-sm">USD</p>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-white">
                      <Check size={16} className="text-cyan-400" />
                      {plan.credits}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white">
                      <Check size={16} className="text-cyan-400" />
                      {plan.premium}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white">
                      <Check size={16} className="text-cyan-400" />
                      {plan.support}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-green-400 font-semibold">
                      <DollarSign size={16} className="text-green-400" />
                      {plan.bonus}
                    </div>
                  </div>

                  {isCurrentPlan(plan) ? (
                    <button disabled className="w-full bg-gray-700/50 text-gray-400 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 cursor-not-allowed border border-gray-600">
                      <Check size={20} />
                      Current Plan
                    </button>
                  ) : isLowerPlan(plan) ? (
                    <button disabled className="w-full bg-gray-800/50 text-gray-500 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 cursor-not-allowed opacity-50">
                      Lower Plan
                    </button>
                  ) : plan.isFree ? (
                    <button disabled className="w-full bg-gray-700/50 text-gray-400 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 cursor-not-allowed">
                      Free Plan
                    </button>
                  ) : (
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => handlePurchasePlan(plan)} className={`w-full bg-gradient-to-r ${plan.buttonColor} text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2`}>
                      {isHigherPlan(plan) ? 'Upgrade Now' : 'Purchase Now'}
                    </motion.button>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Premium Purchase History */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="text-cyan-400" size={20} />
                <h2 className="text-lg font-bold text-white">Purchase History</h2>
              </div>
              
              {premiumTickets.length === 0 ? (
                <div className="bg-gray-800/40 backdrop-blur-xl rounded-2xl p-8 border border-gray-700/50 text-center">
                  <ShoppingBag className="mx-auto text-gray-600 mb-3" size={48} />
                  <p className="text-gray-400">No premium purchases yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {premiumTickets.map((ticket, index) => (
                    <motion.div
                      key={ticket.ticket_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      className="bg-gray-800/40 backdrop-blur-xl rounded-xl p-4 border border-gray-700/50"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-white font-semibold flex items-center gap-2">
                            {ticket.plan_name || 'Premium Plan'}
                            {ticket.status === 'approved' && <Check size={16} className="text-green-400" />}
                            {ticket.status === 'pending' && <Clock size={16} className="text-yellow-400" />}
                            {ticket.status === 'rejected' && <AlertTriangle size={16} className="text-red-400" />}
                          </h4>
                          <p className="text-gray-400 text-xs mt-1">
                            {new Date(ticket.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold">${ticket.amount}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            ticket.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            ticket.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            ticket.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-700/50">
                        <span>ID: {ticket.ticket_id}</span>
                        {ticket.notes && <span className="text-gray-400">{ticket.notes}</span>}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'gates' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gray-800/40 backdrop-blur-xl rounded-2xl p-5 border border-gray-700/50 shadow-lg mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Own Gate Manager</h3>
            </div>

            {/* Gateway Selection */}
            <div className="mb-4">
              <label className="block text-xs text-gray-400 mb-2">Select Gateway</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'shopii', name: 'Shopii', limit: 10 },
                  { id: 'stv1', name: 'ST Auth V1', limit: 5 },
                  { id: 'stv2', name: 'ST Auth V2', limit: 5 },
                  { id: 'stv3', name: 'ST Auth V3', limit: 5 },
                  { id: 'skbased', name: 'SK Based (Own Key)', limit: 0 },
                  { id: 'ppkeybased', name: 'PP KeyBased (Own Key)', limit: 0 }
                ].map((gw) => (
                  <motion.button
                    key={gw.id}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setSelectedPlan(gw.id); // Reusing selectedPlan state for gateway
                      setGateName('');
                      setGateDomain('');
                    }}
                    className={`py-2.5 rounded-lg font-semibold text-sm transition-all border ${
                      selectedPlan === gw.id
                        ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50'
                        : 'bg-gray-800/50 text-gray-400 border-gray-700/50'
                    }`}
                  >
                    {gw.name}
                  </motion.button>
                ))}
              </div>
            </div>



            {/* SK BASED Configuration */}
            {selectedPlan === 'skbased' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">SK Key (Optional)</label>
                    <input 
                      type="text" 
                      placeholder="sk_live_... (leave empty to use default)" 
                      value={gateName} 
                      onChange={(e) => setGateName(e.target.value)} 
                      className="w-full bg-gray-900/50 border-gray-700/50 text-gray-300 placeholder-gray-500 border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500/50" 
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Charge Type</label>
                      <select
                        value={selectedPayment || 'cvv'}
                        onChange={(e) => setSelectedPayment(e.target.value)}
                        className="w-full bg-gray-900/50 border-gray-700/50 text-gray-300 border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500/50"
                      >
                        <option value="cvv">CVV</option>
                        <option value="ccn">CCN</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Amount</label>
                      <select
                        value={gateDomain || '4'}
                        onChange={(e) => setGateDomain(e.target.value)}
                        className="w-full bg-gray-900/50 border-gray-700/50 text-gray-300 border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500/50"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Currency</label>
                    <select
                      className="w-full bg-gray-900/50 border-gray-700/50 text-gray-300 border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500/50"
                      defaultValue="myr"
                    >
                      <option value="myr">MYR - Malaysian Ringgit</option>
                      <option value="usd">USD - US Dollar</option>
                      <option value="eur">EUR - Euro</option>
                      <option value="gbp">GBP - British Pound</option>
                      <option value="aud">AUD - Australian Dollar</option>
                      <option value="cad">CAD - Canadian Dollar</option>
                      <option value="sgd">SGD - Singapore Dollar</option>
                      <option value="inr">INR - Indian Rupee</option>
                      <option value="jpy">JPY - Japanese Yen</option>
                      <option value="hkd">HKD - Hong Kong Dollar</option>
                    </select>
                  </div>

                  <motion.button 
                    whileHover={{ scale: 1.02 }} 
                    whileTap={{ scale: 0.98 }} 
                    onClick={async () => {
                      try {
                        const response = await api.post('/user/save-sk-config', {
                          sk_key: gateName.trim() || null,
                          charge_type: selectedPayment || 'cvv',
                          currency: 'myr',
                          amount: parseInt(gateDomain) || 4
                        });
                        
                        if (response.data.success) {
                          notify.success('SK BASED configuration saved!', 3000);
                          setGateName('');
                          setSelectedPayment(null);
                          setGateDomain('');
                          fetchSavedSKs();
                        }
                      } catch (error) {
                        notify.error(error.response?.data?.error || 'Failed to save configuration', 4000);
                      }
                    }}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"
                  >
                    <Check size={16} />
                    Save SK Configuration
                  </motion.button>
                </div>


                {/* Saved SK Keys */}
                {savedSKs.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-white font-semibold mb-3 text-sm">Saved Keys</h4>
                    <div className="space-y-2">
                      {savedSKs.map((sk) => (
                        <div
                          key={sk._id}
                          className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/50 flex items-center justify-between"
                        >
                          <div>
                            <p className="text-white font-semibold text-sm font-mono">{sk.sk_key_masked}</p>
                            <p className="text-gray-400 text-xs">
                              {sk.charge_type.toUpperCase()} • {sk.currency.toUpperCase()} • Amount: {sk.amount}
                            </p>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeleteSK(sk._id)}
                            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} className="text-red-400" />
                          </motion.button>
                        </div>
                      ))}
                    </div>
                    <p className="text-gray-500 text-xs mt-2">
                      💡 System will randomly select from your SKs when checking
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* PP KeyBased Configuration */}
            {selectedPlan === 'ppkeybased' && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">PayPal Client ID</label>
                  <input
                    type="text"
                    value={gateName}
                    onChange={(e) => setGateName(e.target.value)}
                    placeholder="Enter PayPal Client ID"
                    className="w-full bg-gray-900/50 border-gray-700/50 text-gray-300 placeholder-gray-500 border rounded-lg px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-cyan-500/50"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">PayPal Client Secret</label>
                  <input
                    type="password"
                    value={gateDomain}
                    onChange={(e) => setGateDomain(e.target.value)}
                    placeholder="Enter PayPal Client Secret"
                    className="w-full bg-gray-900/50 border-gray-700/50 text-gray-300 placeholder-gray-500 border rounded-lg px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-cyan-500/50"
                  />
                </div>

                <motion.button 
                  whileHover={{ scale: 1.02 }} 
                  whileTap={{ scale: 0.98 }} 
                  onClick={async () => {
                    if (!gateName.trim() || !gateDomain.trim()) {
                      notify.warning('Please enter both Client ID and Client Secret', 3000);
                      return;
                    }
                    try {
                      const response = await api.post('/user/save-pp-config', {
                        client_id: gateName.trim(),
                        client_secret: gateDomain.trim()
                      });
                      
                      if (response.data.success) {
                        notify.success('PP KeyBased configuration saved!', 3000);
                        setGateName('');
                        setGateDomain('');
                        fetchSavedPPs();
                      }
                    } catch (error) {
                      notify.error(error.response?.data?.error || 'Failed to save configuration', 4000);
                    }
                  }}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"
                >
                  <Check size={16} />
                  Save PP Configuration
                </motion.button>

                {/* Saved PP Keys */}
                {savedPPs.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-white font-semibold mb-3 text-sm">Saved Credentials</h4>
                    <div className="space-y-2">
                      {savedPPs.map((pp) => (
                        <div
                          key={pp._id}
                          className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/50 flex items-center justify-between"
                        >
                          <div>
                            <p className="text-white font-semibold text-sm font-mono">{pp.client_id_masked}</p>
                            <p className="text-gray-400 text-xs">PayPal Credential</p>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeletePP(pp._id)}
                            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} className="text-red-400" />
                          </motion.button>
                        </div>
                      ))}
                    </div>
                    <p className="text-gray-500 text-xs mt-2">
                      💡 System will randomly select from your credentials when checking
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Saved Site Configurations */}
            {savedGates.length > 0 && (
              <div className="mt-6">
                <h4 className="text-white font-semibold mb-3 text-sm">Saved Configurations</h4>
                <div className="space-y-2">
                  {savedGates.map((gate) => (
                    <div
                      key={gate.package_name}
                      className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/50 flex items-center justify-between"
                    >
                      <div>
                        <p className="text-white font-semibold text-sm">{gate.api_name}</p>
                        <p className="text-gray-400 text-xs">
                          {gate.package_name.toUpperCase()} • {gate.site_count} site(s)
                        </p>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDeleteGate(gate.package_name)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} className="text-red-400" />
                      </motion.button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedPlan && selectedPlan !== 'skbased' && selectedPlan !== 'ppkeybased' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-orange-400 flex-shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <p className="font-bold text-white mb-1">
                        {selectedPlan === 'shopii' ? 'Max 10 sites' : 'Max 5 sites'} for {selectedPlan.toUpperCase()}
                      </p>
                      <p className="text-gray-400">
                        {selectedPlan === 'shopii' 
                          ? 'One site per line. Format: https://example.com'
                          : 'Must be FULL LINK to Add Payment Method page'
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">API Name</label>
                    <input 
                      type="text" 
                      placeholder="e.g., My Custom API" 
                      value={gateName} 
                      onChange={(e) => setGateName(e.target.value)} 
                      className="w-full bg-gray-900/50 border-gray-700/50 text-gray-300 placeholder-gray-500 border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500/50" 
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Site Addresses (one per line, max {selectedPlan === 'shopii' ? '10' : '5'})
                    </label>
                    <textarea 
                      placeholder={
                        selectedPlan === 'shopii' 
                          ? `https://example1.com\nhttps://example2.com\nhttps://example3.com`
                          : `https://example.com/my-account/add-payment-method\nhttps://shop2.com/account/payment-methods\nhttps://store3.com/checkout/add-card`
                      }
                      value={gateDomain} 
                      onChange={(e) => setGateDomain(e.target.value)} 
                      rows={6}
                      className="w-full bg-gray-900/50 border-gray-700/50 text-gray-300 placeholder-gray-500 border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500/50 resize-none font-mono"
                    />
                    {selectedPlan !== 'shopii' && (
                      <p className="text-orange-400 text-xs mt-2 flex items-center gap-1">
                        <AlertTriangle size={12} />
                        Must be full link to Add Payment Method page
                      </p>
                    )}
                  </div>

                  <motion.button 
                    whileHover={{ scale: 1.02 }} 
                    whileTap={{ scale: 0.98 }} 
                    onClick={async () => {
                      if (!gateName.trim()) {
                        notify.warning('Please enter API name');
                        return;
                      }
                      
                      if (!gateDomain.trim()) {
                        notify.warning('Please enter at least one site');
                        return;
                      }

                      const sites = gateDomain.split('\n').map(s => s.trim()).filter(s => s);
                      const maxSites = selectedPlan === 'shopii' ? 10 : 5;
                      
                      if (sites.length === 0) {
                        notify.warning('Please enter at least one site');
                        return;
                      }
                      
                      if (sites.length > maxSites) {
                        notify.warning(`Maximum ${maxSites} sites allowed for ${selectedPlan.toUpperCase()}`);
                        return;
                      }

                      try {
                        const response = await api.post('/user/save-auto-sites', {
                          gateway: selectedPlan,
                          apiName: gateName,
                          sites: sites
                        });
                        
                        if (response.data.success) {
                          notify.success(`${sites.length} site(s) saved for ${selectedPlan.toUpperCase()}!`, 3000);
                          setGateName('');
                          setGateDomain('');
                          fetchSavedGates(); // Refresh list
                        }
                      } catch (error) {
                        notify.error(error.response?.data?.error || 'Failed to save sites', 4000);
                      }
                    }}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"
                  >
                    <Check size={16} />
                    Save Sites
                  </motion.button>
                </div>
              </motion.div>
            )}

            {!selectedPlan && savedGates.length === 0 && savedSKs.length === 0 && (
              <div className="text-center py-8">
                <Store size={48} className="text-gray-600 opacity-30 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Select a gateway to manage sites</p>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'message' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gray-800/40 backdrop-blur-xl rounded-2xl p-5 border border-gray-700/50 shadow-lg mb-6">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle size={20} className="text-cyan-400" />
              <h3 className="text-lg font-bold text-white">Message Developer</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">Send a direct message to the developer</p>

            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
              <User size={18} className="text-cyan-400" />
              <div>
                <p className="text-white font-bold text-sm">{user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username || 'User'}</p>
                <p className="text-gray-400 text-xs">ID: {user?.id || 'N/A'}</p>
              </div>
            </div>

            <textarea 
              placeholder="Type your message to the developer..." 
              value={messageText} 
              onChange={(e) => setMessageText(e.target.value)} 
              className="w-full bg-gray-800/30 border-gray-700/50 text-gray-300 placeholder-gray-500 border rounded-xl px-4 py-3 text-sm mb-3 h-32 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500/50" 
            />

            <motion.button 
              whileHover={{ scale: 1.02 }} 
              whileTap={{ scale: 0.98 }} 
              onClick={async () => {
                if (!messageText.trim()) {
                  notify.warning('Please enter a message');
                  return;
                }
                
                try {
                  const response = await api.post('/user/message-developer', { message: messageText });
                  if (response.data.success) {
                    notify.success('Message sent to developer!', 3000);
                    setMessageText('');
                  }
                } catch (error) {
                  notify.error(error.response?.data?.error || 'Failed to send message', 4000);
                }
              }}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
            >
              <Send size={18} />
              Send Message
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Settings;
